/**
 * Scale Drop Modal – Scale or Edit mode (orchestrator).
 * 3-column layout: Config | Creative (Senders + Texts) | Leads.
 * For Media Devoted | Created By Ciro Autiero
 */

import { useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { Copy, Send, Settings2 } from 'lucide-react';

import { LoadingSpinner } from '@/components/ui';

import { useGatewayPerformanceTags, useLeadsUsage, useListPerformanceTags, useTextPerformanceTags } from '@/hooks';
import { useScaleDropForm } from '../../useScaleDropForm';
import { ScaleDropModalLayout } from '../../ScaleDropModalLayout';
import { PasteConfigButton } from '../../PasteConfigButton';
import { useScaleDropEnrichment } from '../../hooks/useScaleDropEnrichment';
import { ScaleOrEditConfigColumn } from './ScaleOrEditConfigColumn';
import { ScaleOrEditCreativeColumn } from './creative';
import { ScaleOrEditLeadsColumn } from './ScaleOrEditLeadsColumn';
import { ScaleOrEditFooter } from './ScaleOrEditFooter';
import type { ScaleDropModalProps } from '../../ScaleDropModal.types';

export function ScaleDropModalScaleOrEdit(props: ScaleDropModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const usageQuery = useLeadsUsage(props.countryKey, props.defaultWorkDate);
  const internalTagsQuery = useListPerformanceTags(props.countryKey, undefined, undefined, !props.perfTagsQuery);
  const perfTagsQuery = props.perfTagsQuery ?? internalTagsQuery;
  const wiserIds = useMemo(() => {
    if (props.wiserIds?.length) return props.wiserIds;
    const fromEdit = props.editDrops?.map((d) => d.wiserId).filter(Number.isFinite) ?? [];
    return fromEdit.length ? [...new Set(fromEdit)] : [];
  }, [props.wiserIds, props.editDrops]);
  const textPerfTagsQuery = useTextPerformanceTags(props.countryKey, wiserIds.length > 0 ? wiserIds : undefined, null, true);
  const gatewayPerfTagsQuery = useGatewayPerformanceTags(props.countryKey, wiserIds.length > 0 ? wiserIds : undefined, null, true);
  const { enrichedLeads, tagFilterOptions } = useScaleDropEnrichment(perfTagsQuery, props.leadsLists);
  const scaleUsageByListId = usageQuery.data ?? {};
  const form = useScaleDropForm({
    ...props,
    mode: (props.mode ?? 'scale') as 'scale' | 'edit',
    editDropId: props.editDropId,
    editDrops: props.editDrops,
    onSave: props.onSave,
    onDeleteSender: props.onDeleteSender,
    onDeleteText: props.onDeleteText,
    onDeleteTextsBulk: props.onDeleteTextsBulk,
  });
  const { isBusy, onClose, mode = 'scale', campaigns, createFromListsWiserId, onCreateFromListsWiserIdChange, onCreateFromLists: _onCreateFromLists } = props;
  const showingLoader = isBusy || isSubmitting;

  const drop = form.drop;
  if (!props.rows.length || !drop) return null;

  const isMulti = props.rows.length > 1;
  const isEdit = mode === 'edit';
  const isCreateFromLists = mode === 'create-from-lists';

  const handleScaleClick = async () => {
    if (showingLoader) return;
    flushSync(() => setIsSubmitting(true));
    await new Promise((r) => setTimeout(r, 0));
    try {
      await form.handleConfirm(onClose);
      if (form.pendingHideTexts.length > 0 && props.onDeleteTextsBulk) {
        await props.onDeleteTextsBulk(form.pendingHideTexts);
      }
      if (form.pendingHideSenders.length > 0 && props.onDeleteSender) {
        await Promise.all(form.pendingHideSenders.map((name) => props.onDeleteSender!(name)));
      }
    } finally { setIsSubmitting(false); }
  };

  return (
    <ScaleDropModalLayout
      title={isEdit ? 'Edit draft' : isCreateFromLists ? `Copy lists as draft — ${props.rows.length} list${props.rows.length !== 1 ? 's' : ''}` : 'Scale & send'}
      icon={isEdit ? <Settings2 className="h-5 w-5" /> : isCreateFromLists ? <Copy className="h-5 w-5" /> : <Send className="h-5 w-5" />}
      onClose={onClose}
      isBusy={showingLoader}
      headerSubtitle={
        isMulti
          ? 'Each drop keeps its own senders/texts. Select below to override for all.'
          : undefined
      }
      headerActions={
        <PasteConfigButton
          onPaste={(v) => form.applyPastedConfig(v)}
          disabled={showingLoader}
        />
      }
      loadingOverlay={showingLoader && !isEdit && !isCreateFromLists ? (
        <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-4 rounded-xl bg-background/95 backdrop-blur-sm" aria-live="polite">
          <LoadingSpinner size="lg" className="text-primary" />
          <p className="text-sm font-medium text-foreground">Scaling and sending...</p>
        </div>
      ) : null}
      footer={
        <ScaleOrEditFooter
          isEdit={isEdit}
          isCreateFromLists={isCreateFromLists}
          showingLoader={showingLoader}
          canConfirm={form.canConfirm}
          onCancel={onClose}
          onConfirm={handleScaleClick}
        />
      }
      configSection={
        <ScaleOrEditConfigColumn
          isEdit={isEdit}
          isCreateFromLists={isCreateFromLists}
          isMulti={isMulti}
          rows={props.rows}
          form={form}
          gateways={props.gateways}
          tlds={props.tlds}
          isBusy={isBusy}
          campaigns={campaigns}
          createFromListsWiserId={createFromListsWiserId}
          onCreateFromListsWiserIdChange={onCreateFromListsWiserIdChange}
          countryKey={props.countryKey}
          tagsByGatewayId={gatewayPerfTagsQuery.data?.tagsByGatewayId}
          tagColors={gatewayPerfTagsQuery.data?.tagColors}
        />
      }
      creativeSection={
        <ScaleOrEditCreativeColumn
          isMulti={isMulti}
          dropCount={props.rows.length}
          rows={props.rows}
          form={form}
          isBusy={isBusy}
          countryKey={props.countryKey}
          onAddSender={props.onAddSender} onAddText={props.onAddText}
          onDeleteTextsBulk={props.onDeleteTextsBulk}
          tagsByTextId={textPerfTagsQuery.data?.tagsByTextId}
          tagColors={textPerfTagsQuery.data?.tagColors}
          smsTextsWithIds={props.smsTextsWithIds}
        />
      }
      leadsSection={
        <ScaleOrEditLeadsColumn
          isMulti={isMulti} form={form} enrichedLeads={enrichedLeads}
          perfTagsQuery={perfTagsQuery} tagFilterOptions={tagFilterOptions}
          scaleUsageByListId={scaleUsageByListId}
        />
      }
    />
  );
}
